import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Trash2, Upload, FileSpreadsheet, ShieldCheck, Store } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  ContactImportItemDto,
  ContactImportReportDto,
  ContactListDto,
  ContactListMemberDto,
  WhatsappCheckReportDto,
} from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Drawer,
  Modal,
  Pagination,
  PhoneInput,
  SearchInput,
  SegmentedControl,
  Table,
  TableContainer,
  TextInput,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import { useDebouncedValue } from '../../utils/useDebouncedValue';
import { parseContacts } from '../../utils/parseContacts';
import DataState from '../DataState';
import {
  PARTNER_BADGE_LABEL,
  WHATSAPP_STATUS_LABEL,
  WHATSAPP_STATUS_TONE,
  isUndeliverable,
} from './messagingLabels';

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;

  > *:first-child {
    flex: 1;
    min-width: 180px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

/**
 * Linha em evidência para quem não deve receber.
 *
 * O fundo vai no `td` e não no `tr` porque a `Table` pinta o hover na célula - sem
 * isso, passar o mouse apagava justamente o destaque que a spec pede.
 */
const Row = styled.tr<{ $blocked: boolean }>`
  ${(p) =>
    p.$blocked &&
    css`
      td {
        background: ${p.theme.error}0f;
      }

      &:hover td {
        background: ${p.theme.error}1f;
      }
    `}
`;

/**
 * Loja do catálogo vinculada ao número, na mesma forma da aba Contatos.
 *
 * Fora da `Stack` de situação de propósito: parceria não é impedimento, é recorte. O
 * vermelho da linha continua valendo só para bloqueado e sem WhatsApp.
 */
const MerchantLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.textMuted};
  }
`;

const MerchantName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 170px;
  padding: 0.15rem 0.5rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  font-size: 0.72rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DrawerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Label = styled.span`
  display: block;
  margin: 1rem 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme.textMuted};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 170px;
  resize: vertical;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font: inherit;
  font-size: 0.88rem;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const Warn = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.79rem;
  line-height: 1.45;
  color: ${(p) => p.theme.warning};
`;

const FileRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.75rem;
  padding: 0.8rem 0.9rem;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;

  input {
    display: none;
  }

  &:hover {
    border-color: ${(p) => p.theme.primary};
    color: ${(p) => p.theme.primary};
  }
`;

const ReportBox = styled.div`
  margin-top: 0.9rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  overflow: hidden;
`;

const ReportSummary = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 0.75rem 0.9rem;
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => p.theme.textSecondary};
`;

const RejectionList = styled.ul`
  margin: 0;
  padding: 0.5rem 0.9rem 0.75rem 1.6rem;
  max-height: 190px;
  overflow-y: auto;
  font-size: 0.8rem;
  line-height: 1.6;
  color: ${(p) => p.theme.textMuted};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type ImportMode = 'manual' | 'paste' | 'file';

const IMPORT_MODES: { value: ImportMode; label: string }[] = [
  { value: 'manual', label: 'Um contato' },
  { value: 'paste', label: 'Colar lista' },
  { value: 'file', label: 'Planilha' },
];

const EMPTY_MANUAL = { name: '', phone: '', company: '', city: '' };

interface ContactListDrawerProps {
  contactList: ContactListDto | null;
  canManage: boolean;
  onClose: () => void;
  /** Avisa a listagem de fundo que a contagem de contatos mudou. */
  onChanged: () => void;
}

/**
 * Contatos de uma lista, em drawer: consultar, importar e remover.
 *
 * Drawer e não navegação porque abrir uma lista para conferir é uma consulta rápida
 * - a listagem continua atrás, e fechar devolve o operador exatamente onde ele
 * estava.
 */
const ContactListDrawer: React.FC<ContactListDrawerProps> = ({
  contactList,
  canManage,
  onClose,
  onChanged,
}) => {
  const { showNotification } = useNotification();

  /**
   * Busca e página carregam o id da lista a que pertencem.
   *
   * Zerar por `useEffect` não bastava, e aqui era pior: o `debouncedSearch` atrasa
   * 350 ms, então a lista B era **sempre** buscada primeiro com o termo digitado na
   * lista A.
   */
  const [view, setView] = useState({ id: contactList?.id ?? null, search: '', page: 0 });
  const currentId = contactList?.id ?? null;
  const current = view.id === currentId ? view : { id: currentId, search: '', page: 0 };
  const search = current.search;
  const page = current.page;
  const [pageSize, setPageSize] = useState(20);
  const debouncedSearch = useDebouncedValue(`${currentId ?? ''}|${search.trim()}`);
  const [debouncedId, debouncedTerm] = debouncedSearch.split('|');
  // Enquanto o debounce ainda carrega o termo da lista anterior, busca sem filtro em
  // vez de aplicar um termo que não é desta lista.
  const effectiveSearch = debouncedId === (currentId ?? '') ? debouncedTerm : '';
  const setSearch = (value: string): void => setView({ id: currentId, search: value, page: 0 });
  const setPage = (value: number): void => setView({ id: currentId, search: current.search, page: value });

  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState<ImportMode>('paste');
  const [pasted, setPasted] = useState('');
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<ContactImportReportDto | null>(null);
  const [removingMember, setRemovingMember] = useState<ContactListMemberDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkReport, setCheckReport] = useState<WhatsappCheckReportDto | null>(null);

  const contactListId = contactList?.id ?? null;

  /**
   * Zera busca e importação ao trocar de lista. Sem isso, abrir a lista B depois de
   * importar 350 linhas na lista A traria essas 350 linhas já no textarea, com o
   * botão pronto dizendo "Importar (350)" - um clique e a lista A ia para dentro da B.
   */
  useEffect(() => {
    setPasted('');
    setReport(null);
    setImportMode('paste');
    setManual(EMPTY_MANUAL);
    setCheckReport(null);
  }, [contactListId]);

  const members = useAsync(
    () =>
      contactListId
        ? backofficeApi.listContactListMembers(contactListId, {
            page,
            size: pageSize,
            query: effectiveSearch || undefined,
          })
        : Promise.resolve(null),
    [contactListId, page, pageSize, effectiveSearch]
  );

  const parsed = useMemo(
    () => (importMode === 'manual' ? null : parseContacts(pasted)),
    [pasted, importMode]
  );

  const itemsToImport: ContactImportItemDto[] = useMemo(() => {
    if (importMode === 'manual') {
      const name = manual.name.trim();
      const phone = manual.phone.trim();
      // Só o WhatsApp é obrigatório: é ele que identifica o contato e é o único
      // sem o qual não há para onde enviar. Sem nome, o backend usa a empresa e,
      // na falta dela, o próprio telefone.
      if (!phone) return [];
      return [
        {
          name: name || undefined,
          phoneNumber: phone,
          company: manual.company.trim() || undefined,
          city: manual.city.trim() || undefined,
        },
      ];
    }
    return parsed?.items ?? [];
  }, [importMode, manual, parsed]);

  const readFile = (file: File): void => {
    const reader = new FileReader();
    reader.onload = () => {
      setPasted(String(reader.result ?? ''));
      setReport(null);
    };
    reader.readAsText(file);
  };

  const runImport = async (): Promise<void> => {
    if (!contactListId || itemsToImport.length === 0) return;
    setImporting(true);
    try {
      const result = await backofficeApi.importContactListMembers(contactListId, {
        items: itemsToImport,
      });
      setReport(result);
      showNotification(
        `${result.imported} contato(s) importado(s)${
          result.duplicated + result.invalid > 0
            ? `, ${result.duplicated + result.invalid} recusado(s)`
            : ''
        }.`,
        result.imported > 0 ? 'success' : 'error'
      );
      if (importMode === 'manual') setManual(EMPTY_MANUAL);
      members.reload();
      onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível importar os contatos.'), 'error');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Revalida a lista inteira contra o provedor.
   *
   * Existe apesar da verificação automática porque o número muda de dono e de estado:
   * quem foi importado semana passada pode ter saído do WhatsApp desde então, e o
   * varredor não volta sozinho (revalidação periódica seria tráfego constante contra
   * o provedor, exatamente o que a spec do disparo evita).
   */
  const runWhatsappCheck = async (): Promise<void> => {
    if (!contactListId) return;
    setChecking(true);
    try {
      const result = await backofficeApi.checkMessagingContactsWhatsapp({ contactListId });
      setCheckReport(result);
      showNotification(
        `${formatNumber(result.checked)} número(s) verificado(s): ${formatNumber(
          result.valid
        )} com WhatsApp, ${formatNumber(result.invalid)} sem.`,
        result.invalid > 0 ? 'error' : 'success'
      );
      members.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível verificar os números.'), 'error');
    } finally {
      setChecking(false);
    }
  };

  const confirmRemoveMember = async (): Promise<void> => {
    if (!contactListId || !removingMember) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteContactListMember(contactListId, removingMember.id);
      showNotification('Contato removido da lista.', 'success');
      setRemovingMember(null);
      members.reload();
      onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o contato.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const page$ = members.data;
  const rows = page$?.content ?? [];

  return (
    <>
      <Drawer
        isOpen={contactList !== null}
        onClose={onClose}
        title={contactList?.name ?? 'Lista'}
        width="720px"
        headerActions={
          canManage ? (
            <DrawerActions>
              <Button variant="outline" onClick={runWhatsappCheck} isLoading={checking}>
                <ShieldCheck size={15} /> Verificar WhatsApp
              </Button>
              <Button variant="primary" onClick={() => setImportOpen(true)}>
                <Upload size={15} /> Adicionar contatos
              </Button>
            </DrawerActions>
          ) : undefined
        }
      >
        <Toolbar>
          <SearchInput
            placeholder="Buscar por nome, empresa ou WhatsApp..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Toolbar>

        {checkReport && (
          <ReportBox>
            <ReportSummary>
              <span>Pedidos: {formatNumber(checkReport.requested)}</span>
              <span>Verificados: {formatNumber(checkReport.checked)}</span>
              <span>Com WhatsApp: {formatNumber(checkReport.valid)}</span>
              <span>Sem WhatsApp: {formatNumber(checkReport.invalid)}</span>
              <span>Não verificados: {formatNumber(checkReport.unverified)}</span>
            </ReportSummary>
            {checkReport.unverified > 0 && (
              <RejectionList>
                <li>
                  Os não verificados continuam com o status que tinham - o provedor não respondeu
                  sobre eles. Tente de novo mais tarde.
                </li>
              </RejectionList>
            )}
          </ReportBox>
        )}

        <DataState
          // `&& !page$`: sem isso, cada tecla da busca trocaria a tabela por esqueletos.
          loading={members.loading && !page$}
          error={members.error}
          isEmpty={rows.length === 0}
          emptyTitle="Nenhum contato nesta lista"
          emptyDescription="Cadastre um a um, cole uma lista de texto ou importe uma planilha."
          onRetry={members.reload}
        >
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Contato</th>
                  <th>WhatsApp</th>
                  <th>Empresa</th>
                  <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                  <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((member) => (
                  <Row key={member.id} $blocked={isUndeliverable(member)}>
                    <td>
                      <strong>{member.name}</strong>
                      {Object.keys(member.attributes).length > 0 && (
                        <FieldHint>
                          {Object.entries(member.attributes)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(' · ')}
                        </FieldHint>
                      )}
                      {member.listsCount > 1 && (
                        <>
                          <FieldHint
                            title={member.otherLists.map((list) => list.name).join(', ')}
                          >
                            Em {formatNumber(member.listsCount)} listas
                          </FieldHint>
                          <Chips>
                            {member.otherLists.map((list) => (
                              <Chip key={list.id} title={list.name}>
                                {list.name}
                              </Chip>
                            ))}
                          </Chips>
                        </>
                      )}
                      {member.merchantName && (
                        <MerchantLine>
                          <Store size={13} />
                          <MerchantName title={member.merchantName}>
                            {member.merchantName}
                          </MerchantName>
                          {member.partner && <Badge tone="primary">{PARTNER_BADGE_LABEL}</Badge>}
                        </MerchantLine>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{member.phoneNumberFormatted}</td>
                    <td>{member.company || '-'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Stack>
                        <Badge tone={WHATSAPP_STATUS_TONE[member.whatsappStatus]}>
                          {WHATSAPP_STATUS_LABEL[member.whatsappStatus]}
                        </Badge>
                        {member.blocked && <Badge tone="danger">Bloqueado · não enviar</Badge>}
                        {member.optedOut && <Badge tone="warning">Opt-out</Badge>}
                        {/* Sem nenhum impedimento não há nada a alertar: o selo verde do
                            WhatsApp já diz que a linha está em ordem. */}
                        {member.blocked && member.blockReason && (
                          <FieldHint>{member.blockReason}</FieldHint>
                        )}
                      </Stack>
                    </td>
                    <td>
                      <RowActions>
                        {canManage && (
                          <ActionButton
                            title="Remover da lista"
                            aria-label="Remover contato"
                            color="#DC2626"
                            onClick={() => setRemovingMember(member)}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        )}
                      </RowActions>
                    </td>
                  </Row>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </DataState>

        {(page$?.totalPages ?? 0) > 1 && (
          <Pagination
            currentPage={page + 1}
            totalPages={page$?.totalPages ?? 1}
            totalItems={page$?.totalElements}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disabled={members.loading}
            onPageChange={(next) => setPage(next - 1)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        )}
      </Drawer>

      <Modal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        title={`Adicionar contatos - ${contactList?.name ?? ''}`}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setImportOpen(false)} disabled={importing}>
              Fechar
            </Button>
            <Button
              variant="primary"
              onClick={runImport}
              isLoading={importing}
              disabled={itemsToImport.length === 0}
            >
              Importar {itemsToImport.length > 0 ? `(${formatNumber(itemsToImport.length)})` : ''}
            </Button>
          </>
        }
      >
        <SegmentedControl options={IMPORT_MODES} value={importMode} onChange={setImportMode} />

        {importMode === 'manual' && (
          <>
            <TextInput
              label="Nome do lojista (opcional)"
              value={manual.name}
              onChange={(e) => setManual((prev) => ({ ...prev, name: e.target.value }))}
              containerStyle={{ marginTop: '0.9rem' }}
            />
            <PhoneInput
              label="WhatsApp do lojista"
              value={manual.phone}
              onChange={(value) => setManual((prev) => ({ ...prev, phone: value }))}
            />
            <TextInput
              label="Empresa (opcional)"
              value={manual.company}
              onChange={(e) => setManual((prev) => ({ ...prev, company: e.target.value }))}
              containerStyle={{ marginTop: '0.75rem' }}
            />
            <TextInput
              label="Cidade (opcional)"
              value={manual.city}
              onChange={(e) => setManual((prev) => ({ ...prev, city: e.target.value }))}
              containerStyle={{ marginTop: '0.75rem' }}
            />
          </>
        )}

        {importMode === 'paste' && (
          <>
            <Label>Uma linha por contato</Label>
            <TextArea
              value={pasted}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setPasted(e.target.value);
                setReport(null);
              }}
              placeholder={'Padaria Central; 41 99999-0001\nOficina do Zé; 45 98888-0002'}
            />
            <FieldHint>
              Separe com ponto e vírgula, vírgula ou tabulação. Se a primeira linha for um cabeçalho
              (Nome; WhatsApp; Empresa; Cidade...), ela é reconhecida e as colunas extras viram tags.
            </FieldHint>
          </>
        )}

        {importMode === 'file' && (
          <>
            <FileRow>
              <FileSpreadsheet size={18} />
              {pasted ? 'Trocar arquivo' : 'Escolher arquivo CSV ou TSV'}
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/plain"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) readFile(file);
                }}
              />
            </FileRow>
            <FieldHint>
              Exporte a planilha como CSV. O arquivo é lido aqui no navegador - você confere o que foi
              entendido antes de enviar.
            </FieldHint>
            {pasted && (
              <>
                <Label>Conteúdo lido</Label>
                <TextArea
                  value={pasted}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPasted(e.target.value)}
                />
              </>
            )}
          </>
        )}

        {importMode !== 'manual' && parsed && parsed.items.length > 0 && (
          <>
            <FieldHint>
              {formatNumber(parsed.items.length)} linha(s) reconhecida(s)
              {parsed.headerDetected ? ', com cabeçalho' : ', sem cabeçalho (Nome; WhatsApp)'}
              {parsed.extraColumns.length > 0
                ? `. Colunas extras como tags: ${parsed.extraColumns.join(', ')}`
                : ''}
              .
            </FieldHint>
            {parsed.missingPhone > 0 && (
              <Warn>
                {formatNumber(parsed.missingPhone)} linha(s) sem WhatsApp reconhecido serão
                recusadas - confira o cabeçalho ou a ordem das colunas antes de importar.
              </Warn>
            )}
            {parsed.missingName > 0 && (
              <FieldHint>
                {formatNumber(parsed.missingName)} linha(s) sem nome. Elas entram assim mesmo:
                o nome vira a empresa ou, na falta dela, o próprio WhatsApp.
              </FieldHint>
            )}
          </>
        )}

        {report && (
          <ReportBox>
            <ReportSummary>
              <span>Recebidas: {formatNumber(report.received)}</span>
              <span>Importadas: {formatNumber(report.imported)}</span>
              <span>Duplicadas: {formatNumber(report.duplicated)}</span>
              <span>Inválidas: {formatNumber(report.invalid)}</span>
            </ReportSummary>
            {report.rejections.length > 0 && (
              <RejectionList>
                {report.rejections.map((rejection) => (
                  <li key={`${rejection.line}-${rejection.phoneNumber ?? ''}`}>
                    Linha {rejection.line}: {rejection.name || 'sem nome'} -{' '}
                    {rejection.phoneNumber || 'sem telefone'} · <strong>{rejection.reason}</strong>
                  </li>
                ))}
              </RejectionList>
            )}
          </ReportBox>
        )}
      </Modal>

      <Modal
        isOpen={removingMember !== null}
        onClose={() => setRemovingMember(null)}
        title="Remover contato"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemovingMember(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemoveMember} isLoading={removing}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{removingMember?.name}</strong> ({removingMember?.phoneNumberFormatted}) desta
          lista? Campanhas já criadas não são afetadas - elas guardam os destinatários no momento em
          que foram montadas.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default ContactListDrawer;
